import { useCallback, useEffect, useState } from "react";
import {
  useSegmentedDriverMetricsLazyQuery,
  SegmentationMethod,
  DriverMode,
  DriverMetricsResponse,
  SegmentedDriverMetricsQueryVariables,
} from "graphql/types/graphql";
import { useHeadroomContext } from "../context/HeadRoomProvider";
import { ApolloError } from "@apollo/client";
import { MAX_ROWS } from "../components/tables/TablePaginationWrapper";

interface UseSegmentedDriverMetricsResult {
  data: DriverMetricsResponse[] | undefined;
  totalCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  loading: boolean;
  error: ApolloError | undefined;
  fetchData: (
    variables?: Partial<SegmentedDriverMetricsQueryVariables>
  ) => Promise<DriverMetricsResponse[] | undefined>;
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  page: number;
  rowsPerPage: number;
}

interface DriverQueryVariables {
  driver1NumeratorColumn?: string;
  driver1DenominatorColumn?: string;
  driver1Mode?: DriverMode;
  driver1Percentile?: number;
  driver2NumeratorColumn?: string;
  driver2DenominatorColumn?: string;
  driver2Mode?: DriverMode;
  driver2Percentile?: number;
}

export const useSegmentedDriverMetrics = (): UseSegmentedDriverMetricsResult => {
  const {
    tableName,
    segmentGroups,
    driver1Type,
    driver1NumeratorType,
    driver1Mode,
    driver2Type,
    driver2NumeratorType,
    driver2Mode,
    customerColumn,
    count1Column,
    driver1Percentile,
    driver2Percentile,
    headroomDriver,
    headroomDriverPercentile,
  } = useHeadroomContext();

  const [cachedData, setCachedData] = useState<DriverMetricsResponse[] | undefined>(undefined);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [hasPreviousPage, setHasPreviousPage] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(MAX_ROWS);

  const offset = (page - 1) * rowsPerPage;

  const hasAllInputs =
    tableName &&
    segmentGroups.length > 0 &&
    segmentGroups.every((group) => group.type) &&
    count1Column &&
    headroomDriver &&
    (headroomDriver === "driver1" ? driver1Type && driver1Mode : driver2Type && driver2Mode);

  const formattedSegmentGroups = segmentGroups.map((group) => ({
    type: group.type,
    method: group.method as SegmentationMethod,
    segments: group.segments.map((segment) => ({
      id: segment.id,
      minValue: segment.minValue,
      maxValue: segment.maxValue,
    })),
    rangeColumn: group.method === SegmentationMethod.Manual ? group.type : undefined,
  }));

  const getQueryVariables = useCallback((): DriverQueryVariables => {
    if (headroomDriver === "driver1") {
      return {
        driver1NumeratorColumn: driver1Mode === DriverMode.Percentage ? driver1NumeratorType : driver1Type,
        driver1DenominatorColumn: driver1Type,
        driver1Mode: driver1Mode as DriverMode,
        driver1Percentile: headroomDriverPercentile,
        driver2NumeratorColumn: undefined,
        driver2DenominatorColumn: undefined,
        driver2Mode: undefined,
        driver2Percentile: undefined,
      };
    } else {
      return {
        driver1NumeratorColumn: undefined,
        driver1DenominatorColumn: undefined,
        driver1Mode: undefined,
        driver1Percentile: undefined,
        driver2NumeratorColumn: driver2Mode === DriverMode.Percentage ? driver2NumeratorType : driver2Type,
        driver2DenominatorColumn: driver2Type,
        driver2Mode: driver2Mode as DriverMode,
        driver2Percentile: headroomDriverPercentile,
      };
    }
  }, [
    headroomDriver,
    driver1Mode,
    driver1NumeratorType,
    driver1Type,
    headroomDriverPercentile,
    driver2Mode,
    driver2NumeratorType,
    driver2Type,
  ]);

  const [fetchSegmentedDriverMetrics, { data, loading, error }] = useSegmentedDriverMetricsLazyQuery({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!loading && !error && data?.segmentedDriverMetrics) {
      setCachedData(data.segmentedDriverMetrics.edges);
      setTotalCount(data.segmentedDriverMetrics.totalCount);
      setHasNextPage(data.segmentedDriverMetrics.hasNextPage);
      setHasPreviousPage(data.segmentedDriverMetrics.hasPreviousPage);
    }
  }, [loading, error, data, page, rowsPerPage, offset]);

  const fetchData = useCallback(
    async (variables?: Partial<SegmentedDriverMetricsQueryVariables>) => {
      if (!hasAllInputs) {
        return undefined;
      }
      const result = await fetchSegmentedDriverMetrics({
        variables: {
          tableName,
          segmentGroups: formattedSegmentGroups,
          customerColumn: count1Column,
          ...getQueryVariables(),
          limit: variables?.limit ?? rowsPerPage,
          offset: variables?.offset ?? offset,
          ...variables,
        },
      });
      return result.data?.segmentedDriverMetrics.edges;
    },
    [
      hasAllInputs,
      fetchSegmentedDriverMetrics,
      tableName,
      formattedSegmentGroups,
      count1Column,
      getQueryVariables,
      rowsPerPage,
      offset,
    ]
  );

  const handleSetPage = useCallback(
    (newPage: number) => {
      const maxPage = Math.max(1, Math.ceil(totalCount / rowsPerPage));
      const boundedPage = Math.min(Math.max(1, newPage), maxPage);
      setPage(boundedPage);
    },
    [totalCount, rowsPerPage]
  );

  const handleSetRowsPerPage = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  return {
    data: data?.segmentedDriverMetrics.edges ?? cachedData,
    totalCount,
    hasNextPage,
    hasPreviousPage,
    loading,
    error,
    fetchData,
    setPage: handleSetPage,
    setRowsPerPage: handleSetRowsPerPage,
    page,
    rowsPerPage,
  };
};
